import { concat, filter, forEach, isEmpty, map, uniq } from 'es-toolkit/compat';
import { Procedure } from 'interfaces/procedure';
import { ProceduresFieldsContext } from 'interfaces/procedure/fields/helpers';
import { Slide } from 'interfaces/slide';

import { createIndividualCasesForSlides } from '../ProceduresPage/usePendingSlides';
import { assignManifestDraftCaseIds, ManifestDialogUrlState } from './dialogState';
import { readManifestRows } from './helpers';
import {
  canOfferFixInCasePlan,
  collectManifestCaseLabelErrors,
  collectManifestValidationErrors,
  resolveUploadedManifestRows,
  unmatchedSlideIdentityMessage,
  UploadedManifestResolution,
} from './manifestResolution';

const MAX_MISSING_SLIDE_ID_ERROR_EXAMPLES = 10;

interface ManifestFileResolution {
  resolution: UploadedManifestResolution;
  errorMessages: string[];
  correction: UploadedManifestResolution | null;
}

export const resolveManifestFileRows = async ({
  file,
  editableExistingAndMockCases,
  existingProcedures,
  pendingCaseCount,
  procedureFieldContext,
  labId,
  studyId,
  fields,
  rowsPerPage,
  fetchSlidesByIdBatches,
  isCurrentUpload,
  excludedRowNumbers,
}: {
  file: File;
  editableExistingAndMockCases: Procedure[];
  existingProcedures: Procedure[];
  pendingCaseCount: number;
  procedureFieldContext: ProceduresFieldsContext;
  labId: string;
  studyId: string;
  fields: Parameters<typeof collectManifestValidationErrors>[0]['fields'];
  rowsPerPage: number;
  fetchSlidesByIdBatches: (slideIds: string[]) => Promise<Slide[]>;
  isCurrentUpload: () => boolean;
  excludedRowNumbers?: number[];
}): Promise<ManifestFileResolution | null> => {
  const excelRows = await readManifestRows(file);
  if (!isCurrentUpload()) return null;
  let resolutionInput = {
    editableExistingAndMockCases,
    notEditableSlidesMockCases: [] as Procedure[],
    excelRows,
    procedureFieldContext,
    labId,
    studyId,
    excludedRowNumbers,
  };
  let resolution = resolveUploadedManifestRows(resolutionInput);
  if (!isEmpty(resolution.slideIdsToFetch)) {
    const slideIdsToFetch = resolution.slideIdsToFetch;
    const fetchedSlides = await fetchSlidesByIdBatches(slideIdsToFetch);
    if (!isCurrentUpload()) return null;
    const fetchedSlideIds = new Set(map(fetchedSlides, 'id'));
    const unmatchedSlideIds = slideIdsToFetch.filter((slideId) => !fetchedSlideIds.has(slideId));
    const unmatchedErrors = map(unmatchedSlideIds.slice(0, MAX_MISSING_SLIDE_ID_ERROR_EXAMPLES), (slideId) =>
      unmatchedSlideIdentityMessage(`Slide ID ${slideId}`)
    );
    if (unmatchedSlideIds.length > MAX_MISSING_SLIDE_ID_ERROR_EXAMPLES) {
      unmatchedErrors.push(
        `${
          unmatchedSlideIds.length - MAX_MISSING_SLIDE_ID_ERROR_EXAMPLES
        } additional Slide IDs were not matched to lost and found slides in this lab or study. Select the slides from Lab lost and found slides or Slides awaiting assignment.`
      );
    }
    resolutionInput = {
      ...resolutionInput,
      notEditableSlidesMockCases: map(createIndividualCasesForSlides(fetchedSlides, labId), (mockCase, index) => ({
        ...mockCase,
        id: pendingCaseCount + index,
      })),
    };
    resolution = resolveUploadedManifestRows(resolutionInput);
    if (!isEmpty(unmatchedErrors)) {
      return { resolution, errorMessages: [...resolution.errorMessages, ...unmatchedErrors], correction: null };
    }
  }
  const errorMessages = uniq([
    ...resolution.errorMessages,
    ...collectManifestCaseLabelErrors(resolution.manifestCases, existingProcedures),
    ...collectManifestValidationErrors({ cases: resolution.manifestCases, fields, procedureFieldContext, rowsPerPage }),
  ]);
  if (isEmpty(errorMessages)) return { resolution, errorMessages, correction: null };
  const preservedResolution = isEmpty(resolution.manifestCases)
    ? resolveUploadedManifestRows({ ...resolutionInput, preserveInvalidEditableValues: true })
    : resolution;
  const canFix =
    canOfferFixInCasePlan(errorMessages) &&
    !isEmpty(preservedResolution.manifestCases) &&
    isEmpty(preservedResolution.errorMessages) &&
    isEmpty(preservedResolution.notIngestedSlides) &&
    isEmpty(preservedResolution.slideIdsToFetch);
  return { resolution, errorMessages, correction: canFix ? preservedResolution : null };
};

export const replaceManifestDraftFromUpload = ({
  previousState,
  resolution,
  existingProcedures,
}: {
  previousState: ManifestDialogUrlState;
  resolution: UploadedManifestResolution;
  existingProcedures: Procedure[];
}): ManifestDialogUrlState => {
  const uploadOutcomeWarnings = isEmpty(resolution.manifestCases)
    ? [
        ...resolution.warningMessages,
        isEmpty(resolution.notIngestedSlides)
          ? 'No manifest rows were generated from this file. Check the slide identifiers and upload again.'
          : 'No manifest rows were generated from this file. Review the Not Ingested Slides tab or update the slide identifiers and upload again.',
      ]
    : resolution.warningMessages;
  const uploadedTargets: NonNullable<ManifestDialogUrlState['existingCaseAssignmentsBySlideId']> = {};
  const explicitNewCaseSlideIds = new Set(resolution.explicitNewCaseSlideIds);
  forEach(resolution.manifestCases, (procedure) => {
    const matchingCases = filter(existingProcedures, { label: procedure.label });
    forEach(procedure.slides, (slide) => {
      uploadedTargets[slide.id] = explicitNewCaseSlideIds.has(slide.id)
        ? 'new'
        : matchingCases.length === 1
        ? matchingCases[0].id
        : matchingCases.length
        ? null
        : 'new';
    });
  });
  return {
    ...previousState,
    existingCaseAssignmentsBySlideId: uploadedTargets,
    uploadedManifestCases: assignManifestDraftCaseIds({
      cases: resolution.manifestCases,
      retainedCases: concat(previousState.uploadedManifestCases || [], previousState.manualManifestCases || []),
      casePatchesById: previousState.casePatchesById,
    }),
    uploadedNotIngestedSlides: resolution.notIngestedSlides,
    uploadedWarningMessages: uploadOutcomeWarnings,
    uploadedErrorMessages: [],
    manualManifestCases: [],
    manualManifestSlideIdsToLoad: [],
    manualManifestGroupIntoSingleCase: false,
    manualManifestErrorMessages: [],
    casePatchesById: {},
    slidePatchesById: {},
    removedSlideIds: [],
  };
};
